import { cx } from "@/utils/cx";
import type { ReactNode } from "react";

/**
 * Gymkee 2027 fact row (spec fact-row 1.0; sheet DS06).
 *
 * A label and its value on one line: the label at the left in tertiary, the value at the right
 * in primary and tabular when it is a number, so a record's facts read as a quiet list rather
 * than as a table. 52 tall, a hairline under each row but the last, and no side padding of its
 * own because the sheet already pads. A long value wraps to a second line rather than
 * truncating, and a fact that has no value is drawn as the en dash in tertiary, never as "-".
 */

export interface FactRowProps {
  label: ReactNode;
  /** A second line under the label, in tertiary; the quantity and the unit price. */
  sublabel?: ReactNode;
  value?: ReactNode;
  /** A second line under the value, in tertiary; an email under a name. */
  subvalue?: ReactNode;
  /** Tabular figures and weight 600, for money and for a count. */
  numeric?: boolean;
  /** Weight 600 on the label too: the last line of a totals group. */
  strong?: boolean;
  /** The trailing control after the value (the download icon button). */
  action?: ReactNode;
  className?: string;
}

export function FactRow({
  label,
  sublabel,
  value,
  subvalue,
  numeric = false,
  strong = false,
  action,
  className,
}: FactRowProps) {
  const empty = value === undefined || value === null || value === "";

  return (
    <div
      className={cx(
        "ds-fact-row gap-gk-4 ds-hairline flex min-h-[var(--gk-row-default)] items-center justify-between border-b py-gk-2 last:border-b-0",
        className,
      )}
    >
      <dt className="min-w-0 flex-1">
        <span
          className={cx(
            "block text-[14px]",
            strong
              ? "font-semibold text-[var(--gk-text-primary)]"
              : "text-[var(--gk-text-secondary)]",
          )}
        >
          {label}
        </span>
        {sublabel && (
          <span className="block text-[13px] text-[var(--gk-text-tertiary)]">
            {sublabel}
          </span>
        )}
      </dt>
      <dd className="gap-gk-2 flex min-w-0 items-center justify-end text-right">
        <span className="min-w-0">
          <span
            className={cx(
              "block break-words",
              numeric && "gk-num",
              strong ? "text-[16px] font-semibold" : "text-[14px]",
              empty
                ? "text-[var(--gk-text-tertiary)]"
                : "text-[var(--gk-text-primary)]",
              numeric && !strong && "font-medium",
            )}
          >
            {/* The empty mark is the en dash in tertiary; the old "-" placeholder is gone. */}
            {empty ? "–" : value}
          </span>
          {subvalue && (
            <span className="block text-[13px] text-[var(--gk-text-tertiary)]">
              {subvalue}
            </span>
          )}
        </span>
        {action}
      </dd>
    </div>
  );
}

/** The list around fact rows: a description list, so a screen reader hears each pair together. */
export function FactList({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <dl className={cx("flex flex-col", className)}>{children}</dl>;
}
